import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
// Imported from the standalone leaf file, NOT from ReportFormattingModal.
// TableComponent (copilotComponents) pulls this pill in, so importing from a
// genbi/ module would create a copilotComponents -> genbi -> ... edge and webpack
// evaluated the default export before initialisation. utils/ColorPickerButton has
// no app-level imports, so the graph stays acyclic.
import ColorPickerButton from '../../utils/ColorPickerButton';

/* ===========================================================================
   RichFormatPill — floating selection toolbar for GenBI response editing.

   Emits ONLY markup the report pipeline can parse:

     Markdown body  (md_parser.py + color_filter.lua)
       **bold**  _italic_  <u>underline</u>
       <span style="color:#RRGGBB;">
       <span style="background-color:#RRGGBB;">
       both together -> ONE span, never nested. color_filter.lua's
       flatten_inline() does not carry colour through a nested Span, so a
       nested span silently loses its style in the DOCX.

     Table cells   (structured_parser.py)
       inline: <b> <i> <u>            (parse_element tag branches)
       cell:   <td style="background-color:..; color:..; font-weight:bold;
                          font-style:italic; text-decoration:underline;
                          text-align:..">   (parse_cell_style)
       Inline <span style> inside a cell is NOT supported by parse_element,
       so colour/highlight are offered at CELL level only.
       text-align is ignored by apply_cell_formatting for header/footer rows,
       so the alignment control is disabled there.
   =========================================================================== */

/* ── Markdown inline helpers (exported: used by GenbiResponseComponent) ───── */

const SPAN_WHOLE_RE = /^<span style="([^"]*)">([\s\S]*)<\/span>$/;

/** Regex to grab the trailing <span style="..."> at end of a string. */
const SPAN_OPEN_TAIL_RE = /<span style="([^"]*)">$/;

/**
 * True when `text` contains a <span style="..."> that is never closed,
 * OR a </span> that was never opened — i.e. the selection crosses a
 * span boundary and any formatting applied here would nest or break spans.
 */
const hasPartialSpanOverlap = (before, sel, after) => {
  // Count unmatched open/close tags across the three slices.
  // Simple approach: if `before` ends inside an open span (unclosed),
  // or `sel` itself contains a partial open/close, we have overlap.
  const unclosedInBefore = (() => {
    let depth = 0;
    const opens = before.match(/<span style="[^"]*">/g) || [];
    const closes = before.match(/<\/span>/g) || [];
    depth = opens.length - closes.length;
    return depth > 0;
  })();
  if (unclosedInBefore) return true;

  // Selection itself straddles a boundary: contains an open or close tag
  const selHasOpen = /<span style="[^"]*">/.test(sel);
  const selHasClose = /<\/span>/.test(sel);
  // If both present, check it's a complete span (whole match) — that's fine
  if (selHasOpen && selHasClose) {
    if (!SPAN_WHOLE_RE.test(sel)) return true;    // partial / nested
  } else if (selHasOpen || selHasClose) {
    return true;                                  // only one side = crossing
  }

  return false;
};

export const parseSpanStyle = (styleStr) => {
  const out = { color: null, bg: null };
  if (!styleStr) return out;
  const bg = styleStr.match(/background-color\s*:\s*([^;]+)/i);
  if (bg) out.bg = bg[1].trim();
  const fg = styleStr.match(/(?<![-\w])color\s*:\s*([^;]+)/i);
  if (fg) out.color = fg[1].trim();
  return out;
};

/** Build a single span carrying both properties, or plain text when neither. */
export const buildSpan = (inner, props) => {
  const decls = [];
  if (props.color) decls.push(`color:${props.color};`);
  if (props.bg) decls.push(`background-color:${props.bg};`);
  if (!decls.length) return inner;
  return `<span style="${decls.join('')}">${inner}</span>`;
};

const WRAPPERS = {
  bold: { open: '**', close: '**' },
  italic: { open: '_', close: '_' },
  underline: { open: '<u>', close: '</u>' },
};

/**
 * Peel all B/I/U wrappers off a selection regardless of nesting order.
 * Works whether markers sit inside the selection or straddle it (outside).
 * Returns { before, sel, after, found: { bold, italic, underline } }.
 */
const peelBIUMarks = (before, sel, after) => {
  const found = { bold: false, italic: false, underline: false };
  let changed = true;
  while (changed) {
    changed = false;
    for (const k of Object.keys(WRAPPERS)) {
      if (found[k]) continue;                        // each mark peeled once
      const { open, close } = WRAPPERS[k];
      if (sel.startsWith(open) && sel.endsWith(close) && sel.length >= open.length + close.length) {
        sel = sel.slice(open.length, sel.length - close.length);   // strip inside
        found[k] = true;
        changed = true;
      } else if (before.endsWith(open) && after.startsWith(close)) {
        before = before.slice(0, before.length - open.length);     // strip outside
        after = after.slice(close.length);
        found[k] = true;
        changed = true;
      }
    }
  }
  return { before, sel, after, found };
};

/** Consistent nesting order: bold innermost → italic → underline outermost. */
const WRAP_ORDER = ['bold', 'italic', 'underline'];

/**
 * Detect B/I/U marks that straddle the selection — open marker somewhere in
 * `before`, close marker somewhere in `after`, but NOT at the immediate
 * boundary (peelBIUMarks only catches boundary-adjacent markers).
 *
 * Returns { bold: bool, italic: bool, underline: bool } — true means that
 * mark already wraps the selection from outside and applying the same mark
 * again would nest it (e.g. **enterprise **sales** optimization**).
 *
 * For bold (`**`): scan backwards from the end of `before` for `**` that
 * isn't at the boundary (already handled by peelBIUMarks). Same forward
 * in `after` for the closing `**`. Since `**` is symmetric, an even count
 * of `**` in `before` means we're OUTSIDE bold; odd means INSIDE.
 *
 * For italic (`_`): same parity check on `_`.
 * For underline (`<u>`/`</u>`): depth counting.
 */
const detectStraddlingMarks = (before, after) => {
  const result = { bold: false, italic: false, underline: false };

  // Bold: count `**` occurrences in before. Odd = we're inside bold.
  // Must not count `***` (bold+italic) as two separate `**` — split on
  // non-`*` boundaries. Simple approach: count non-overlapping `**`.
  const boldCount = (before.match(/\*\*/g) || []).length;
  if (boldCount % 2 === 1 && (after.match(/\*\*/g) || []).length > 0) {
    result.bold = true;
  }

  // Italic: count `_` that are markdown italic markers (not inside words).
  // Simplistic parity: odd count of standalone `_` = inside italic.
  const italicCount = (before.match(/(^|[^\\])_/g) || []).length;
  if (italicCount % 2 === 1 && (after.match(/_/g) || []).length > 0) {
    result.italic = true;
  }

  // Underline: depth counting of <u> / </u>
  const uOpens = (before.match(/<u>/gi) || []).length;
  const uCloses = (before.match(/<\/u>/gi) || []).length;
  if (uOpens > uCloses && (after.match(/<\/u>/gi) || []).length > 0) {
    result.underline = true;
  }

  return result;
};

const wrapWithMarks = (text, marks) => {
  let result = text;
  for (const k of WRAP_ORDER) {
    if (marks[k]) result = `${WRAPPERS[k].open}${result}${WRAPPERS[k].close}`;
  }
  return result;
};

/**
 * Inspect the selected slice and report which marks are already on it.
 * Handles arbitrarily nested B/I/U markers both inside and outside the
 * selection, so all active marks are detected regardless of application order.
 */
export const readTextMarks = (md, start, end) => {
  const sel = md.slice(start, end);
  const before = md.slice(0, start);
  const after = md.slice(end);
  const marks = { bold: false, italic: false, underline: false, color: null, bg: null };

  // Peel all B/I/U layers to detect every active mark
  const peeled = peelBIUMarks(before, sel, after);
  marks.bold = peeled.found.bold;
  marks.italic = peeled.found.italic;
  marks.underline = peeled.found.underline;

  // Also detect marks that straddle the selection (open in before, close
  // in after, but not at the immediate boundary — peelBIUMarks misses these).
  const straddling = detectStraddlingMarks(peeled.before, peeled.after);
  if (straddling.bold)      marks.bold = true;
  if (straddling.italic)    marks.italic = true;
  if (straddling.underline) marks.underline = true;

  // Colour / highlight — check on the ORIGINAL boundaries (span sits outside
  // or alongside B/I/U marks, and is handled by its own branch in applyTextFormat)
  const whole = sel.match(SPAN_WHOLE_RE);
  if (whole) {
    const p = parseSpanStyle(whole[1]);
    marks.color = p.color;
    marks.bg = p.bg;
    // B/I/U may be inside the span — peelBIUMarks can't see through span
    // tags, so peel the inner content separately and merge.
    const innerPeeled = peelBIUMarks('', whole[2], '');
    if (innerPeeled.found.bold)      marks.bold = true;
    if (innerPeeled.found.italic)    marks.italic = true;
    if (innerPeeled.found.underline) marks.underline = true;
  } else {
    const openTag = before.match(/<span style="([^"]*)">$/);
    if (openTag && after.startsWith('</span>')) {
      const p = parseSpanStyle(openTag[1]);
      marks.color = p.color;
      marks.bg = p.bg;
      // Selection is the full inner content of the span — peel it for B/I/U
      const innerPeeled = peelBIUMarks('', sel, '');
      if (innerPeeled.found.bold)      marks.bold = true;
      if (innerPeeled.found.italic)    marks.italic = true;
      if (innerPeeled.found.underline) marks.underline = true;
    }
  }
  return marks;
};

/**
 * Apply one formatting action to md[start:end].
 *
 * action: 'bold' | 'italic' | 'underline' | 'color' | 'bg'
 * value : hex string for color/bg, or null to clear that property
 *
 * Returns { md, start, end } with the selection range re-anchored onto the
 * rewritten slice so the caller can restore it in the textarea.
 */
export const applyTextFormat = (md, start, end, action, value) => {
  let before = md.slice(0, start);
  let sel = md.slice(start, end);
  let after = md.slice(end);

  if (action === 'bold' || action === 'italic' || action === 'underline') {
    // Peel every B/I/U layer off the selection (handles any nesting order)
    const peeled = peelBIUMarks(before, sel, after);
    before = peeled.before;
    after = peeled.after;

    // ── Span-aware wrapping: B/I/U must go INSIDE any existing span ────
    // B/I/U inside a span is perfectly parseable by color_filter.lua
    // (flatten_inline recurses into Strong/Emph/Underline inside a Span).
    // We only need to ensure the markers land inside, never outside.
    const bareText = peeled.sel;

    // ── Guard: detect B/I/U marks that straddle the selection ──────────
    // peelBIUMarks only sees marks at the immediate boundary. If the same
    // mark wraps a LARGER range that includes the selection (e.g. user
    // selected "sales motion" inside "**enterprise sales motion opt**"),
    // applying that mark again would nest it. Block with warning.
    const straddling = detectStraddlingMarks(before, after);
    if (straddling[action]) {
      return { md, start, end, warning: 'This formatting will not be parsed correctly in the report.' };
    }

    // Case A: bare text IS a complete span → peel and wrap the inner content.
    // peelBIUMarks (outer) can only see marks outside/around the span tags,
    // NOT marks inside.  We must peel the inner content separately, merge
    // both detections, then apply the toggle on the true combined state.
    const spanMatch = bareText.match(SPAN_WHOLE_RE);
    if (spanMatch) {
      const spanStyle = spanMatch[1];
      const innerPeeled = peelBIUMarks('', spanMatch[2], '');
      // True state = outer detected OR inner detected
      const merged = {
        bold:      peeled.found.bold      || innerPeeled.found.bold,
        italic:    peeled.found.italic    || innerPeeled.found.italic,
        underline: peeled.found.underline || innerPeeled.found.underline,
      };
      merged[action] = !merged[action];          // toggle ONCE
      const wrapped = wrapWithMarks(innerPeeled.sel, merged);
      sel = `<span style="${spanStyle}">${wrapped}</span>`;
      const nextStart = before.length;
      return { md: before + sel + after, start: nextStart, end: nextStart + sel.length };
    }

    // For all non-span cases, toggle on the outer-peel result
    const marks = { ...peeled.found };
    marks[action] = !marks[action];              // toggle ONCE

    // Case B: selection sits inside a span (open tag somewhere in before,
    // close tag somewhere in after). B/I/U is fine here — just wrap the
    // selected text; the surrounding span stays intact.
    const outerOpen = before.match(SPAN_OPEN_TAIL_RE);
    if (outerOpen && after.startsWith('</span>')) {
      sel = wrapWithMarks(bareText, marks);
      const nextStart = before.length;
      return { md: before + sel + after, start: nextStart, end: nextStart + sel.length };
    }
    // Partial text inside span — before has unclosed <span> but doesn't
    // end with the tag (there's text between tag and selection).
    // B/I/U is still safe inside the span body.
    const unclosedSpanInBefore = (() => {
      const opens = (before.match(/<span style="[^"]*">/g) || []).length;
      const closes = (before.match(/<\/span>/g) || []).length;
      return opens > closes;
    })();
    const unclosedSpanClosesInAfter = /<\/span>/.test(after);
    if (unclosedSpanInBefore && unclosedSpanClosesInAfter) {
      sel = wrapWithMarks(bareText, marks);
      const nextStart = before.length;
      return { md: before + sel + after, start: nextStart, end: nextStart + sel.length };
    }

    // Case C: selection crosses a span boundary (contains partial open/close
    // tags) — this produces broken nesting, block it.
    const selHasPartialSpan = (/<span style="[^"]*">/.test(bareText) || /<\/span>/.test(bareText))
      && !SPAN_WHOLE_RE.test(bareText);
    if (selHasPartialSpan) {
      return { md, start, end, warning: 'This formatting will not be parsed correctly in the report.' };
    }

    // Case D: no span involved — default behavior
    sel = wrapWithMarks(bareText, marks);

    const nextStart = before.length;
    return { md: before + sel + after, start: nextStart, end: nextStart + sel.length };
  }

  // colour / highlight — always collapse to ONE span
  let props = { color: null, bg: null };
  let inner = sel;

  const whole = sel.match(SPAN_WHOLE_RE);
  if (whole) {
    props = parseSpanStyle(whole[1]);
    inner = whole[2];
  } else {
    const openTag = before.match(/<span style="([^"]*)">$/);
    if (openTag && after.startsWith('</span>')) {
      props = parseSpanStyle(openTag[1]);
      before = before.slice(0, before.length - openTag[0].length);
      after = after.slice('</span>'.length);
    }
  }

  // ── Guard: block if selection partially overlaps an existing span ────
  // If neither SPAN_WHOLE_RE nor the before/after open-tag check matched,
  // `inner === sel` (no span was detected). Check whether we'd be creating
  // a nested span inside an existing one, or crossing a span boundary.
  if (inner === sel && hasPartialSpanOverlap(before, sel, after)) {
    return { md, start, end, warning: 'This formatting will not be parsed correctly in the report.' };
  }

  props[action] = value || null;                 // null clears the property
  const rebuilt = buildSpan(inner, props);
  const nextStart = before.length;
  return { md: before + rebuilt + after, start: nextStart, end: nextStart + rebuilt.length };
};

/**
 * Viewport rect of md[start:end] inside a <textarea>.
 * A textarea has no Range API, so we mirror it in an off-screen div that copies
 * every layout-affecting style, then measure a span wrapping the selection.
 */
export const getTextareaSelectionRect = (ta, start, end) => {
  if (!ta) return null;
  const cs = window.getComputedStyle(ta);
  const mirror = document.createElement('div');
  const COPY = [
    'boxSizing', 'width', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
    'letterSpacing', 'lineHeight', 'textTransform', 'wordSpacing', 'textIndent',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  ];
  COPY.forEach((p) => { mirror.style[p] = cs[p]; });
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.overflowWrap = 'break-word';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';

  const value = ta.value || '';
  mirror.textContent = value.slice(0, start);
  const marker = document.createElement('span');
  marker.textContent = value.slice(start, end) || '.';
  mirror.appendChild(marker);
  mirror.appendChild(document.createTextNode(value.slice(end)));
  document.body.appendChild(mirror);

  const taRect = ta.getBoundingClientRect();
  const rect = {
    left: taRect.left + marker.offsetLeft - ta.scrollLeft,
    top: taRect.top + marker.offsetTop - ta.scrollTop,
    width: marker.offsetWidth,
    height: marker.offsetHeight,
  };
  document.body.removeChild(mirror);
  return rect;   // raw viewport rect; visibility is decided by getVisibleClip
};

/**
 * The visible band an element actually occupies on screen.
 *
 * There are two scrollbars in the studio: the editor's own, and the outer page
 * / panel one. Checking the viewport alone is not enough — scrolling the outer
 * bar slides the editor up behind the sticky toolbar while the selection rect
 * still reports top > 0, which is how the pill ended up floating over the
 * header. So we intersect the rects of every scrollable ancestor (each one is a
 * real clipping edge) and finally the viewport.
 */
export const getVisibleClip = (el) => {
  let top = 0;
  let bottom = window.innerHeight;
  let left = 0;
  let right = window.innerWidth;
  let node = el;
  while (node && node !== document.body && node.nodeType === 1) {
    const cs = window.getComputedStyle(node);
    if (/(auto|scroll|hidden)/.test(`${cs.overflowY} ${cs.overflowX}`)) {
      const r = node.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
      left = Math.max(left, r.left);
      right = Math.min(right, r.right);
    }
    node = node.parentElement;
  }
  return { top, bottom, left, right };
};

/* ── Presentational pill ──────────────────────────────────────────────────── */

// Bold / italic / underline are not listed: their toolbar buttons already
// toggle off on a second click, so a duplicate remove entry adds nothing.
// 'cellbg' only exists in cell mode — markdown has no cell to fill.
const removeItems = (isCell) => [
  { key: 'color', label: 'Remove text colour', icon: 'bi-palette' },
  { key: 'bg', label: 'Remove highlight', icon: 'bi-highlighter' },
  ...(isCell ? [{ key: 'cellbg', label: 'Remove cell fill', icon: 'bi-grid-3x3' }] : []),
];

const ALIGNMENTS = [
  { key: 'left', icon: 'bi-text-left', label: 'Align left' },
  { key: 'center', icon: 'bi-text-center', label: 'Align center' },
  { key: 'right', icon: 'bi-text-right', label: 'Align right' },
  { key: 'justify', icon: 'bi-justify', label: 'Justify' },
];

const Swatch = ({ letter, icon, colour }) => (
  <span className="rft-swatch">
    {icon
      ? <i className={`bi ${icon} rft-swatch__icon`} />
      : <span className="rft-swatch__letter">{letter}</span>}
    <span className="rft-swatch__bar" style={{ background: colour || 'transparent' }} />
  </span>
);

const RichFormatPill = ({
  visible,
  anchorRect,          // DOMRect-like in viewport coords, or null
  clipEl = null,       // editing surface; its scrollable ancestors bound the pill
  mode = 'text',       // 'text' | 'cell'
  marks = {},          // { bold, italic, underline, color, bg }
  cellState = {},      // { textColor, bgColor, align, alignDisabled, cellCount }
  onToggle,            // (key) => void   key: bold|italic|underline
  onColor,             // (hex|null) => void  inline text colour
  onHighlight,         // (hex|null) => void  inline text highlight
  onCellBg,            // (hex|null) => void  <td> background, cell mode only
  onAlign,             // (key) => void
  onRemove,            // (key) => void
}) => {
  const [removeOpen, setRemoveOpen] = useState(false);
  const pillRef = useRef(null);

  // ── Stable portal container ───────────────────────────────────────────
  // A persistent <div> is appended to document.body once and never removed
  // until the component unmounts. The portal always renders into it, and
  // visibility is controlled with display:none instead of returning null.
  // This prevents the React "removeChild" crash that occurs when rapid
  // show/hide cycles cause the portal target to go out-of-sync with the
  // real DOM (e.g. concurrent selectionchange + mousedown clearing pillRect
  // while React is still committing the previous portal mount).
  const portalRef = useRef(null);
  if (!portalRef.current && typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.className = 'rft-pill-portal';
    div.style.position = 'fixed';
    div.style.top = '0';
    div.style.left = '0';
    div.style.width = '0';
    div.style.height = '0';
    div.style.overflow = 'visible';
    div.style.zIndex = '9999';
    div.style.pointerEvents = 'none';
    document.body.appendChild(div);
    portalRef.current = div;
  }
  useEffect(() => {
    return () => {
      if (portalRef.current && portalRef.current.parentNode) {
        portalRef.current.parentNode.removeChild(portalRef.current);
        portalRef.current = null;
      }
    };
  }, []);

  useEffect(() => { if (!visible) setRemoveOpen(false); }, [visible]);

  useEffect(() => {
    if (!removeOpen) return undefined;
    const onDocDown = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) setRemoveOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [removeOpen]);

  // Determine whether the pill should actually be shown
  let shouldShow = false;
  let pillTop = 0;
  let pillLeft = 0;
  let flip = false;

  if (anchorRect) {
    const PILL_GAP = 8;
    const PILL_H = 44;
    const rectBottom = anchorRect.top + anchorRect.height;
    const clip = getVisibleClip(clipEl);

    // Never clamp the pill into view — clamping is what made it stick to the top
    // of the page. Outside its clip band it simply does not render, so it can
    // neither drift nor overlap the toolbar.
    if (rectBottom >= clip.top + 2 && anchorRect.top <= clip.bottom - 2) {
      shouldShow = true;
      // Flip below the selection when there is no room above it inside the band.
      flip = anchorRect.top - PILL_H - PILL_GAP < clip.top;
      pillLeft = Math.round(Math.min(
        Math.max(anchorRect.left + anchorRect.width / 2, clip.left + 140),
        clip.right - 140
      ));
      pillTop = Math.round(flip ? rectBottom + PILL_GAP : anchorRect.top - PILL_GAP);
    }
  }

  // Keep the caret/selection alive: preventDefault on mousedown stops the
  // browser from moving focus (and clearing the selection) before onClick runs.
  const hold = (e) => e.preventDefault();

  const isCell = mode === 'cell';

  const node = (
    <div
      ref={pillRef}
      className={`rft-pill-anchor rft-pill-anchor--fixed ${flip ? 'rft-pill-anchor--below' : 'rft-pill-anchor--above'} ${shouldShow ? 'rft-pill-anchor--visible' : ''}`}
      style={{
        '--rft-top': `${pillTop}px`,
        '--rft-left': `${pillLeft}px`,
        top: pillTop,
        left: pillLeft,
        display: shouldShow ? '' : 'none',
        pointerEvents: shouldShow ? 'auto' : 'none',
      }}
      onMouseDown={hold}
    >
      <div className="rft-pill">
        <div className="rft-pill__shell ev-card-3 d-inline-flex align-items-center">

          {['bold', 'italic', 'underline'].map((k) => (
            <button
              key={k}
              type="button"
              title={k.charAt(0).toUpperCase() + k.slice(1)}
              className={`ev-btn-text-only-fill rft-fmt-btn ${marks[k] ? 'rft-fmt-btn--active' : ''}`}
              onMouseDown={hold}
              onClick={() => onToggle && onToggle(k)}
            >
              <span className={`rft-label rft-label--${k.charAt(0)}`}>
                {k.charAt(0).toUpperCase()}
              </span>
            </button>
          ))}

          <span className="rft-sep" />

          {/* Group 2 — inline text colour + highlight. Both modes write a span:
              markdown via applyTextFormat, cells via execCommand. */}
          <span className="rft-color-slot" onMouseDown={hold}>
            <ColorPickerButton
              value={marks.color || '#000000'}
              onChange={(c) => onColor && onColor(c)}
              icon={<Swatch letter="A" colour={marks.color} />}
            />
          </span>

          <span className="rft-color-slot" onMouseDown={hold}>
            <ColorPickerButton
              value={marks.bg || '#FFFF00'}
              onChange={(c) => onHighlight && onHighlight(c)}
              icon={<Swatch icon="bi-highlighter" colour={marks.bg} />}
            />
          </span>

          {/* Group 3 — cell fill. Cell mode only: this writes background-color on
              the <td>, which has no equivalent in markdown. */}
          {isCell && (
            <>
              <span className="rft-sep" />
              <span className="rft-color-slot" onMouseDown={hold}>
                <ColorPickerButton
                  value={cellState.bgColor || '#EEEEEE'}
                  onChange={(c) => onCellBg && onCellBg(c)}
                  icon={<Swatch icon="bi bi-paint-bucket" colour={cellState.bgColor} />}
                />
              </span>
              <span className="rft-sep" />
              {ALIGNMENTS.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  title={cellState.alignDisabled
                    ? 'Alignment is not applied to header/footer cells in the report'
                    : a.label}
                  disabled={cellState.alignDisabled}
                  className={`ev-btn-text-only-fill rft-fmt-btn ${cellState.align === a.key ? 'rft-fmt-btn--active' : ''}`}
                  onMouseDown={hold}
                  onClick={() => onAlign && onAlign(a.key)}
                >
                  <i className={`bi ${a.icon} rft-label`} />
                </button>
              ))}
              <span className="rft-sep" />
              <button
                type="button"
                title="Bullet list"
                className={`ev-btn-text-only-fill rft-fmt-btn ${marks.unorderedList ? 'rft-fmt-btn--active' : ''}`}
                onMouseDown={hold}
                onClick={() => onToggle && onToggle('insertUnorderedList')}
              >
                <i className="bi bi-list-ul rft-label" />
              </button>
              <button
                type="button"
                title="Numbered list"
                className={`ev-btn-text-only-fill rft-fmt-btn ${marks.orderedList ? 'rft-fmt-btn--active' : ''}`}
                onMouseDown={hold}
                onClick={() => onToggle && onToggle('insertOrderedList')}
              >
                <i className="bi bi-list-ol rft-label" />
              </button>
            </>
          )}

          <span className="rft-sep" />

          <span className="rft-remove-wrap">
            <button
              type="button"
              title="Remove formatting"
              className="rft-clear-btn"
              onMouseDown={hold}
              onClick={() => setRemoveOpen((v) => !v)}
            >
              <i className="bi bi-eraser" />
            </button>

            {removeOpen && (
              <div className="rft-remove-menu ev-card-3">
                {/* Inside a table these clear the td style; in markdown they
                    clear the property on the surrounding span. */}
                {removeItems(isCell).map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    className="rft-remove-menu__item"
                    onMouseDown={hold}
                    onClick={() => { setRemoveOpen(false); onRemove && onRemove(it.key); }}
                  >
                    <i className={`bi ${it.icon}`} />
                    <span>{it.label}</span>
                  </button>
                ))}
              </div>
            )}
          </span>

          {isCell && cellState.cellCount > 1 && (
            <span className="rft-cell-count">{cellState.cellCount} cells</span>
          )}
        </div>
        <span className="rft-pill__caret" />
      </div>
    </div>
  );

  return createPortal(node, portalRef.current);
};

export default RichFormatPill;
